import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Loader2, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const { t, lang } = useLanguage();
  const tc = t.contact;

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error(tc.toastFill);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      if (data.email_status === "mocked") {
        toast.success(tc.toastReceived, { description: tc.toastReceivedDesc });
      } else if (data.email_status === "failed") {
        toast.warning(tc.toastSavedFail, { description: tc.toastSavedFailDesc });
      } else {
        toast.success(tc.toastSent, { description: tc.toastSentDesc });
      }
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(tc.toastError, {
        description: err?.response?.data?.detail || tc.toastErrorDesc,
      });
    } finally {
      setLoading(false);
    }
  };

  const fieldClass =
    "bg-white/5 border-white/10 text-zinc-100 placeholder:text-zinc-500 focus-visible:ring-[#E6007E] focus-visible:ring-offset-0 h-12 rounded-xl";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-32 md:py-40 border-t border-white/5">
      <div className="mx-auto max-w-6xl px-6 md:px-10 grid md:grid-cols-12 gap-12">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="md:col-span-5"
        >
          <p className="text-xs tracking-[0.22em] uppercase text-[#00A6E6] mb-4">{tc.kicker}</p>
          <h2 className="font-display text-4xl md:text-5xl font-light tracking-tighter text-balance">
            {lang === "ar" ? (
              <>تنشر مجلة؟ <span className="italic">لنتحدث.</span></>
            ) : (
              <>Publishing a magazine? <span className="italic">Let's talk.</span></>
            )}
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed">
            {tc.sub}
          </p>
          <a
            href="mailto:Info@paigez.ai"
            data-testid="contact-email-link"
            className="mt-8 inline-flex items-center gap-3 text-zinc-200 hover:text-white"
          >
            <span className="inline-flex items-center justify-center h-10 w-10 rounded-full glass">
              <Mail size={16} className="text-[#E6007E]" />
            </span>
            Info@paigez.ai
          </a>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="md:col-span-7 glass rounded-3xl p-8 md:p-10 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="name" className="text-xs uppercase tracking-widest text-zinc-400 mb-2 block">
                {tc.name}
              </Label>
              <Input
                id="name"
                data-testid="contact-input-name"
                value={form.name}
                onChange={setField("name")}
                placeholder={tc.phName}
                className={fieldClass}
                required
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-zinc-400 mb-2 block">
                {tc.email}
              </Label>
              <Input
                id="email"
                data-testid="contact-input-email"
                type="email"
                value={form.email}
                onChange={setField("email")}
                placeholder={tc.phEmail}
                className={fieldClass}
                required
              />
            </div>
          </div>
          <div>
            <Label htmlFor="subject" className="text-xs uppercase tracking-widest text-zinc-400 mb-2 block">
              {tc.subject}
            </Label>
            <Input
              id="subject"
              data-testid="contact-input-subject"
              value={form.subject}
              onChange={setField("subject")}
              placeholder={tc.phSubject}
              className={fieldClass}
              required
            />
          </div>
          <div>
            <Label htmlFor="message" className="text-xs uppercase tracking-widest text-zinc-400 mb-2 block">
              {tc.message}
            </Label>
            <Textarea
              id="message"
              data-testid="contact-input-message"
              value={form.message}
              onChange={setField("message")}
              placeholder={tc.phMessage}
              rows={5}
              className="bg-white/5 border-white/10 text-zinc-100 placeholder:text-zinc-500 focus-visible:ring-[#E6007E] focus-visible:ring-offset-0 rounded-xl resize-none"
              required
            />
          </div>
          <button
            type="submit"
            data-testid="contact-submit"
            disabled={loading}
            className="group inline-flex items-center gap-2 rounded-full bg-white text-black px-7 py-4 font-medium hover:bg-zinc-200 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" /> {tc.sending}
              </>
            ) : (
              <>
                {tc.send}
                <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1 rtl:-scale-x-100 rtl:group-hover:-translate-x-1" />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
